import domainRepository from '@/api/domainRepository';
import { IBmsAppDependencies, IBmsDomainShort } from '@/models/domain.model';
import { domainStore } from '@/store/domain-store';
import { computed } from 'vue';
import { bms } from '@/composables/general/useDomainSettings';
const domainid = computed(() => domainStore.getState().domainid);

export default function useMapWizard() {
  const map: {
    baseUrl: string;
    type: string;
    lat: number;
    lon: number;
    zoom: number;
    maxZoom?: number;
    minZoom?: number;
    type_parameters?: any;
    wmsZoomLevels?: any;
    wmsBaseLayerUrls?: IBmsDomainShort['map']['wms_base_layer_urls'];
    attribution?: string;
    cluster_message_markers?: number;
    create_marker_uri: string;
  } = {
    baseUrl: 'http://tile.openstreetmap.org/{z}/{x}/{y}.png',
    // TODO: get create_marker_uri from somewhere or define it at a better place?
    create_marker_uri: 'http://localhost/wdw/r/000/static/img/marker/marker-new.png',
    type: 'osm',
    lat: 49.870577,
    lon: 8.652393,
    zoom: 16,
  };
  function getSingleMarkerLayer(messageId: number, domainEndpoint: string) {
    return {
      group: 'Marker',
      title: 'Meldung',
      opacity: 1,
      type: 'marker_overlay',
      url: domainEndpoint + '/message/' + messageId + '/item_geojson',
      visible: true,
      preventClickOnLayerFeatures: true,
    };
  }

  // Helper function to handle additional layers
  function handleAdditionalLayers(mapSettingsSources: any[]) {
    const result: any = [];
    mapSettingsSources.forEach((s: any) => {
      result.push({
        base: s.base == 1,
        format: s.format,
        group: s.group,
        opacity: s.opacity,
        style: s.style,
        title: s.title,
        type: s.type,
        url: s.url,
        visible: s.visible == 1,
      });
    });
    return result;
  }

  // Helper function to handle extra layers
  function handleExtraLayers(extraLayers: any[]) {
    const result: any = [];

    let base_url = process.env.VUE_APP_SERVER_BASE_URL;
    if (base_url == '/') {
      base_url = window.location.protocol + '//' + window.location.host;
    }
    extraLayers.forEach(s => {
      result.push({
        base: 0,
        format: 'geojson',
        group: 'Gebiete',
        opacity: 1,
        style: s.style,
        title: s.geometry.display_name,
        type: 'geojson_overlay',
        url: base_url + s.geometry.geojson_uri,
        visible: 1,
      });
    });

    return result;
  }

  function mapConfig(
    theDomainEndpoint: string,
    mapsettings: IBmsDomainShort['map'],
    defaultMarkerLayer?: boolean,
    singleMarkerLayerId = 0,
    extraLayers?: any[]
  ) {
    let bounds = undefined;
    let {
      baseUrl: baseMapUrl,
      lat,
      lon,
      zoom,
      maxZoom,
      minZoom,
      type: mapType,
      type_parameters: mapParams,
      wmsZoomLevels,
      wmsBaseLayerUrls,
      attribution,
      cluster_message_markers,
    } = map;
    if ('bounds' in mapsettings && mapsettings.bounds.length != 0) {
      bounds = mapsettings.bounds;
    }

    if ('position' in mapsettings) {
      if ('lat' in mapsettings.position) {
        lat = mapsettings.position.lat;
      }

      if ('lon' in mapsettings.position) {
        lon = mapsettings.position.lon;
      }
    }

    if ('zoom' in mapsettings) {
      if ('default' in mapsettings.zoom) {
        zoom = mapsettings.zoom.default;
      }
      if ('max' in mapsettings.zoom) {
        maxZoom = mapsettings.zoom.max;
      }
      if ('min' in mapsettings.zoom) {
        minZoom = mapsettings.zoom.min;
      }
    }

    if ('tiles' in mapsettings) {
      if ('url' in mapsettings.tiles && mapsettings.tiles.url) {
        baseMapUrl = mapsettings.tiles.url;
      }
    }

    if ('bounds' in mapsettings && mapsettings.bounds.length != 0) {
      bounds = mapsettings.bounds;
    }

    if ('type' in mapsettings && mapsettings.type) {
      mapType = mapsettings.type;
    }

    if ('type_parameters' in mapsettings && mapsettings.type_parameters) {
      mapParams = mapsettings.type_parameters;
    }

    if ('wms_layers_zoom_levels' in mapsettings && mapsettings.wms_layers_zoom_levels) {
      wmsZoomLevels = mapsettings.wms_layers_zoom_levels;
    }

    if ('wms_base_layer_urls' in mapsettings && mapsettings.wms_base_layer_urls) {
      wmsBaseLayerUrls = mapsettings.wms_base_layer_urls;
    }

    if ('attribution' in mapsettings && mapsettings.attribution) {
      attribution = mapsettings.attribution;
    }

    if ('cluster_message_markers' in mapsettings && mapsettings.cluster_message_markers) {
      cluster_message_markers = mapsettings.cluster_message_markers;
    }

    const sources: any = [];

    // Handle base layers or fallback to default base layer

    // Handling wmsBaseLayerUrls or baseMapUrl if no other sources are present
    if (wmsBaseLayerUrls && Object.keys(wmsBaseLayerUrls).length > 0) {
      // We have multiple sources of maps.

      // order layers alphabetically by key
      const ordered_layers = Object.keys(wmsBaseLayerUrls)
        .sort()
        .reduce(function (result: any, key: any) {
          result[key] = wmsBaseLayerUrls![key];
          return result;
        }, {});
      Object.entries(ordered_layers).forEach(([key, value]: any) => {
        const baseLayer: any = {
          base: true,
          crossorigin: false,
          group: 'Karten',
          title: key,
          type: 'map_overlay',
          url: value.url,
          visible: true,
          attribution: attribution,
        };
        // siteadmin -> Mängelmelder -> Kartentyp (WMS-Karte) || wmsParams
        if (value.mapType == 'wms' || (!value.mapType && mapType == 'wms') || value.wmsParams) {
          baseLayer.type = 'map_overlay_wms';
          baseLayer.wmsParams = value.wmsParams;
        } else {
          baseLayer.url = baseLayer.url.replace('${z}', '{z}');
          baseLayer.url = baseLayer.url.replace('${y}', '{y}');
          baseLayer.url = baseLayer.url.replace('${x}', '{x}');
        }
        sources.push(baseLayer);
      });
    } else {
      // No other map sources
      const baseLayer: any = {
        base: true,
        crossorigin: false,
        group: 'Karten',
        title: 'Basiskarte',
        type: 'map_overlay',
        url: baseMapUrl,
        visible: true,
        attribution: attribution,
      };
      if (mapType == 'wms') {
        baseLayer.type = 'map_overlay_wms';
        baseLayer.wmsParams = mapParams;
      }
      if (mapType == 'xyz') {
        baseLayer.type = 'map_overlay_xyz';
        baseLayer.xyzParams = mapParams;
      } else {
        // Replace placeholders in URL if not wms or xyz type
        baseLayer.url = baseLayer.url.replace('${z}', '{z}');
        baseLayer.url = baseLayer.url.replace('${y}', '{y}');
        baseLayer.url = baseLayer.url.replace('${x}', '{x}');
      }
      sources.push(baseLayer);
    }
    // Handle additional layers from mapsettings
    if ('sources' in mapsettings && mapsettings.sources.length > 0) {
      sources.push(handleAdditionalLayers(mapsettings.sources));
    }

    // Handle extra layers if provided
    if (extraLayers !== undefined && extraLayers.length > 0) {
      sources.push(handleExtraLayers(extraLayers));
    }
    // Handle single marker layer if singleMarkerLayerId is provided
    if (singleMarkerLayerId && singleMarkerLayerId > 0) {
      sources.push(getSingleMarkerLayer(singleMarkerLayerId, theDomainEndpoint!));
    }

    if (defaultMarkerLayer) {
      const markerLayer = {
        group: 'Marker',
        title: 'Meldungen',
        opacity: 1,
        type: 'marker_overlay',
        url: theDomainEndpoint + '/message/geojson',
        forceParams: {
          rows: 200,
          sort: '-id',
        },
        visible: true,
      };
      sources.push(markerLayer);
    } else {
      const markerLayerUrl =
        mapsettings.marker_layer_url || `/api/v1/domain/${domainid.value}/message/geojson?sort=-created`;
      const markerLayer = {
        opacity: 1,
        type: 'marker_overlay',
        // remove /api/v1 so that it isnt double (gets added by axios)
        url: markerLayerUrl.slice(7),
        visible: true,
      };
      sources.push(markerLayer);
    }
    return {
      bounds,
      lat,
      lon,
      maxZoom,
      minZoom,
      renderer: 'Message',
      zoom,
      sources,
      cluster_message_markers,
      create_marker_uri: map.create_marker_uri,
    };
  }

  function checkBmsAppDependencies(location: { lat: number; lon: number }): Promise<IBmsAppDependencies> {
    const domainid = domainStore.getState().domainid;
    return new Promise((resolve, reject) => {
      // TODO set this var correctly, see Wizard
      const checkIndividualTypes = false;
      const data = {
        // If domain has default == 1 and allow_default == 0
        allowNoDefault: false,

        // If a different domain should be used for this position
        differentDomain: false,
        differentDomainHref: '',
        differentDomainTitle: '',

        showNoNew: false,
        showMessageNotPossible: false,
      };
      if (bms.appid) {
        domainRepository
          .getBmsAppDomainByQueryParams(bms.appid, location)
          .then((response: any) => {
            if (checkIndividualTypes) {
              // check we have categories for this domain
              let contains_own_types = false;
              const types = response.types;
              for (let i = 0; i < types.length; i++) {
                const type = types[i];
                if (type.domain.id == domainid) {
                  contains_own_types = true;
                  break;
                }
              }
              if (contains_own_types) {
                // Reset anything that has been set
                data.allowNoDefault = false;
                data.differentDomain = false;
                data.differentDomainTitle = '';
                data.differentDomainHref = '';
              } else {
                data.differentDomain = true;
                data.differentDomainTitle = response.primary_domain.name;
                data.differentDomainHref = response.primary_domain.links.web_message_create.href;
              }
            } else {
              if (response.is_default && !response.allow_default) {
                data.allowNoDefault = true;
              } else if (response.primary_domain.id !== domainid) {
                data.differentDomain = true;
                data.differentDomainTitle = response.primary_domain.name;
                data.differentDomainHref = response.primary_domain.links.web_message_create.href;
              } else {
                // Reset anything that has been set
                data.allowNoDefault = false;
                data.differentDomain = false;
                data.differentDomainHref = '';
                data.differentDomainTitle = '';
              }
            }
          })
          .catch((error: unknown) => {
            console.log(error);
            reject(error);
          })
          .finally(() => {
            resolve(data);
          });
      }
    });
  }
  return {
    checkBmsAppDependencies,
    mapConfig,
  };
}
